import React from "react";

import { Refresh } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DIAGNOSTICS_MAX_HOURS,
  rangeHours,
  type TelemetryChartMetric,
  type TelemetryEventKind,
} from "@/lib/telemetry";
import { CHART_METRICS, METRIC_LABEL } from "@/components/telemetry/SeriesCharts";
import {
  formatSeconds,
  fromLocalInputValue,
  toLocalInputValue,
  type TelemetryTz,
} from "@/components/telemetry/format";

export const PRESETS = [
  { id: "1h", label: "1h", hours: 1 },
  { id: "6h", label: "6h", hours: 6 },
  { id: "24h", label: "24h", hours: 24 },
  { id: "48h", label: "48h", hours: 48 },
  { id: "7d", label: "7d", hours: 168 },
] as const;

export type PresetId = (typeof PRESETS)[number]["id"] | "custom";

export const EVENT_KINDS: { id: TelemetryEventKind; label: string }[] = [
  { id: "state_change", label: "State changes" },
  { id: "gap", label: "Gaps" },
  { id: "command", label: "Commands" },
  { id: "anomaly", label: "Anomalies" },
  { id: "sample", label: "All samples" },
];

/** Re-exported so the control bar and the charts can never drift apart. */
export const METRICS: TelemetryChartMetric[] = CHART_METRICS;

export interface TelemetryControlsProps {
  unitId: string;
  preset: PresetId;
  from: string;
  to: string;
  kinds: TelemetryEventKind[];
  metrics: TelemetryChartMetric[];
  tz: TelemetryTz;
  isFetching: boolean;
  onPreset: (preset: PresetId) => void;
  onRange: (range: { from: string; to: string }) => void;
  onKinds: (kinds: TelemetryEventKind[]) => void;
  onMetrics: (metrics: TelemetryChartMetric[]) => void;
  onTz: (tz: TelemetryTz) => void;
  onRefresh: () => void;
}

/**
 * Range and unit choose the backend window. Event/metric toggles are view
 * filters over that window, so support can explore without paying a new Athena
 * scan per click. "All samples" is the exception: it asks for a heavier scan.
 *
 * There is no auto-refresh anywhere in this feature: each refresh is a paid
 * Athena scan, and a background interval on a support tab left open overnight
 * is a bill, not a convenience.
 */
export function TelemetryControls({
  unitId,
  preset,
  from,
  to,
  kinds,
  metrics,
  tz,
  isFetching,
  onPreset,
  onRange,
  onKinds,
  onMetrics,
  onTz,
  onRefresh,
}: TelemetryControlsProps) {
  const hours = from && to ? rangeHours(from, to) : 0;
  const wide = hours > DIAGNOSTICS_MAX_HOURS;

  return (
    <div className="space-y-3 rounded-xl border border-border bg-card p-3">
      <div className="flex flex-wrap items-end gap-3">
        {/* The charger is the page, not a control. This panel is mounted under
            /unit/<id>, so a second way to change which unit is being read could
            only ever disagree with the route — and typing an id into it bought a
            fresh Athena scan for whatever was typed. */}
        <div>
          <Label>Charger</Label>
          <div
            className="mt-1 font-mono text-sm leading-9"
            data-testid="telemetry-unit-id"
          >
            {unitId}
          </div>
        </div>

        <div>
          <Label>Range</Label>
          <ToggleGroup
            className="mt-1"
            type="single"
            size="sm"
            variant="outline"
            value={preset}
            onValueChange={(value) => value && onPreset(value as PresetId)}
          >
            {PRESETS.map((p) => (
              <ToggleGroupItem key={p.id} value={p.id}>
                {p.label}
              </ToggleGroupItem>
            ))}
            <ToggleGroupItem value="custom">Custom</ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div>
          <Label>Times</Label>
          <ToggleGroup
            className="mt-1"
            type="single"
            size="sm"
            variant="outline"
            value={tz}
            onValueChange={(value) => value && onTz(value as TelemetryTz)}
          >
            <ToggleGroupItem value="local">Local</ToggleGroupItem>
            <ToggleGroupItem value="utc">UTC</ToggleGroupItem>
          </ToggleGroup>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="ml-auto"
          onClick={onRefresh}
          disabled={isFetching}
          data-testid="telemetry-refresh"
        >
          <Refresh className="h-4 w-4" /> {isFetching ? "Querying…" : "Refresh"}
        </Button>
      </div>

      {preset === "custom" && (
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="telemetry-from">From (local)</Label>
            <Input
              id="telemetry-from"
              type="datetime-local"
              className="mt-1 w-[220px]"
              value={toLocalInputValue(from)}
              onChange={(e) => {
                const iso = fromLocalInputValue(e.target.value);
                if (iso) onRange({ from: iso, to });
              }}
            />
          </div>
          <div>
            <Label htmlFor="telemetry-to">To (local)</Label>
            <Input
              id="telemetry-to"
              type="datetime-local"
              className="mt-1 w-[220px]"
              value={toLocalInputValue(to)}
              onChange={(e) => {
                const iso = fromLocalInputValue(e.target.value);
                if (iso) onRange({ from, to: iso });
              }}
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <div>
          <Label>Events</Label>
          <ToggleGroup
            className="mt-1 flex-wrap"
            type="multiple"
            size="sm"
            variant="outline"
            value={kinds}
            onValueChange={(value) => onKinds((value as TelemetryEventKind[]) ?? [])}
          >
            {EVENT_KINDS.map((kind) => (
              <ToggleGroupItem key={kind.id} value={kind.id}>
                {kind.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div>
          <Label>Metrics</Label>
          <ToggleGroup
            className="mt-1 flex-wrap"
            type="multiple"
            size="sm"
            variant="outline"
            value={metrics}
            onValueChange={(value) => onMetrics((value as TelemetryChartMetric[]) ?? [])}
          >
            {METRICS.map((metric) => (
              <ToggleGroupItem key={metric} value={metric}>
                {METRIC_LABEL[metric].replace(/\s*\(.*\)$/, "")}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      <div className="space-y-1 text-[11px] text-muted-foreground">
        <div data-testid="telemetry-filter-note">
          Event and metric toggles filter the window already loaded — they apply instantly and cost
          nothing. Refresh runs a new lake query.
        </div>
        <div>
          Window {formatSeconds(hours * 3600)}
          {wide && (
            <>
              {" "}
              — wider than the {DIAGNOSTICS_MAX_HOURS} h single-scan limit, so coverage, series and
              events are queried separately (three scans instead of one).
            </>
          )}
        </div>
        {kinds.includes("sample") && (
          <div data-testid="telemetry-samples-note">
            “All samples” is the one toggle that is not a filter: raw samples are not in the
            standard scan, so it runs a heavier lake query and returns every archived row —
            expensive and rarely readable.
          </div>
        )}
      </div>
    </div>
  );
}
